import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import useEvent from './useEvent';
import useMapRef from './useMapRef';
import useWindowEvent from './useWindowEvent';

export type AutoListHandler<T extends HTMLElement = HTMLElement> = ReturnType<
  typeof useAutoAppendList<T>
>;

type CountInput = { itemCount: number } | { list: unknown[] };

type FocusOrigin = {
  fieldIndex: number;
  target: HTMLElement;
};

const FOCUS_EL = ['input', 'button', 'select', 'textarea'] as const;

const focusElSelector = FOCUS_EL.join(', ');

const getFocusableFields = <T extends HTMLElement = HTMLElement>(el: T | undefined) => {
  if (!el) return [];

  const nodes = el.querySelectorAll(focusElSelector);

  return Array.from(nodes).filter((node) => node instanceof HTMLElement);
};

type Input = CountInput & {
  onAppend?: () => void;
};

const useAutoAppendList = <T extends HTMLElement = HTMLElement>(input: Input) => {
  // a single interaction can focus the placeholder more than once
  // fe clicking a MUI Joy Autocomplete
  const blockDupeFocusRef = useRef(false);
  const focusOriginRef = useRef<FocusOrigin | null>(null);

  const itemsRef = useMapRef<number, T>();

  const itemCount = 'itemCount' in input ? input.itemCount : input.list.length;

  const focusLastItemField = useCallback(
    (fieldIndex: number) => {
      if (!itemCount) return;

      const lastItem = itemsRef.current().get(itemCount - 1);

      const field = getFocusableFields(lastItem)[fieldIndex];

      field?.focus();
    },
    [itemCount, itemsRef],
  );

  const createPlaceholderFocusHandler = useCallback(
    (onAppend: () => void): React.FocusEventHandler<T> =>
      (e) => {
        const fields = getFocusableFields(e.currentTarget);

        let fieldIndex = fields.indexOf(e.target);

        if (fieldIndex === -1) fieldIndex = 0;

        if (blockDupeFocusRef.current) {
          focusLastItemField(fieldIndex);

          return;
        }

        blockDupeFocusRef.current = true;
        focusOriginRef.current = { fieldIndex, target: e.target };

        onAppend();
      },
    [focusLastItemField],
  );

  const handlePlaceholderFocus: React.FocusEventHandler<T> = useEvent((e) => {
    if (!input.onAppend) return;

    createPlaceholderFocusHandler(input.onAppend)(e);
  });

  const resetFocusInteraction = () => {
    blockDupeFocusRef.current = false;
  };

  useWindowEvent('click', resetFocusInteraction);
  useWindowEvent('keydown', resetFocusInteraction, true);
  useWindowEvent('pointerdown', resetFocusInteraction, true);

  useLayoutEffect(() => {
    const focusOrigin = focusOriginRef.current;

    if (!focusOrigin) return;

    focusOriginRef.current = null;

    focusLastItemField(focusOrigin.fieldIndex);

    // no-op if focus moved to the newly added item
    // will remove focus if none was added and placeholder still holds it
    focusOrigin.target.blur();
  }, [focusLastItemField]);

  return useMemo(
    () => ({
      createPlaceholderFocusHandler,
      handlePlaceholderFocus,
      registerItem: itemsRef.createCallback,
    }),
    [createPlaceholderFocusHandler, handlePlaceholderFocus, itemsRef.createCallback],
  );
};

export default useAutoAppendList;
